import React, { useEffect, useState } from 'react';
import { Grid } from '../../app/StyleGuide';
import { ActionRowButton, LinkStyleButton } from '../../components/Buttons';
import { CarouselItem } from '../../components/CarouselElements';
import {
  getButtonLinkType,
  redirectToSearchLinkUrl,
  redirectToTrainingLinkUrl,
} from '../../components/CatalogElements';
import ErrorMessage from '../../components/ErrorMessage';
import HelpAndInfoBox from '../../components/HelpAndInfoBox';
import { Container, ContainerFluid } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import SectionTitle from '../../components/SectionTitle';
import windowService from '../../services/windowService';
import { deleteRecentlyPaired, getRecentlyPaired, NewPurchasesOverlay } from '../pairing';
import TrainingSubMenu from '../training/components/TrainingSubMenu';
import { getCatalogHomePage } from './api/getCatalogHomePage';
import FeatureCarousel from './components/FeatureCarousel';
import SearchBar from './components/SearchBar';
import TrainingCompilationCarousel from './components/TrainingCompilationCarousel';
import TrainingCompilationGrid from './components/TrainingCompilationGrid';
import ViewAllTraining from './components/ViewAllTraining';
import { Catalog } from './models/catalog';

export function TrainingCatalog() {
  const [data, setData] = useState<{
    isLoading: boolean;
    isError?: boolean;
    catalog?: Catalog;
  }>({
    isLoading: true,
  });

  useEffect(() => {
    getCatalogHomePage()
      .then(catalog => {
        setData({ catalog: catalog, isLoading: false });
      })
      .catch(error => {
        console.error(error);
        setData(() => ({ isLoading: false, isError: true }));
      });
    return () => {
      deleteRecentlyPaired();
    };
  }, []);

  return (
    <>
      <div>
        <TrainingSubMenu activeTab={'catalog'} />
      </div>
      {data.isLoading ? (
        <Container>
          <LoadingState />
        </Container>
      ) : data.isError ? (
        <Container>
          <ErrorMessage>
            A problem occurred showing this page. Please refresh the page to try again. <a href="#/help">Contact Us</a>
          </ErrorMessage>
        </Container>
      ) : (
        <>
          <Container style={{ margin: `${Grid._6} auto` }}>
            <SearchBar />
          </Container>
          <ContainerFluid style={{ margin: `${Grid._6} auto` }}>
            <FeatureCarousel
              initialFirstItem={(data.catalog?.featureCarouselSection?.items?.length ?? 0) > 2 ? 1 : 0}
              focusOnSelect={true}
            >
              {data.catalog?.featureCarouselSection?.items?.length &&
                data.catalog?.featureCarouselSection?.items?.length > 0 &&
                data.catalog.featureCarouselSection.items.map(carouselItem => (
                  <CarouselItem key={carouselItem.id} item={carouselItem} canRedirect />
                ))}
            </FeatureCarousel>
          </ContainerFluid>
          {data.catalog?.compilationsSection?.compilations?.length &&
            data.catalog?.compilationsSection?.compilations?.length > 0 &&
            data.catalog.compilationsSection.compilations.map(compilation => (
              <Container
                data-qa-hook="compilationContainer"
                style={{ margin: `${Grid._12} auto` }}
                key={compilation._id}
              >
                <SectionTitle title={compilation.name} subtitle={compilation.shortDescription}>
                  <div>
                    <LinkStyleButton
                      data-qa-hook="compilationViewAll"
                      onClick={() => windowService.redirectTo(`#/catalog/compilation/${compilation._id}`)}
                    >
                      View All
                    </LinkStyleButton>
                  </div>
                </SectionTitle>
                {compilation.trainingItems.length ? (
                  <>
                    {compilation.catalogComponentStyle.toLowerCase() === 'grid' && (
                      <TrainingCompilationGrid
                        compilationId={compilation._id}
                        trainingItems={compilation.trainingItems}
                      />
                    )}
                    {compilation.catalogComponentStyle.toLowerCase() === 'carousel' && (
                      <TrainingCompilationCarousel
                        compilationId={compilation._id}
                        trainingItems={compilation.trainingItems}
                      />
                    )}
                  </>
                ) : (
                  <HelpAndInfoBox
                    title="Training is on the way"
                    description="Authors are curating content for this compilation and it will be visible here soon."
                  />
                )}
              </Container>
            ))}
          {data.catalog?.buttonsSection?.buttons && data.catalog.buttonsSection.buttons.length > 0 && (
            <Container style={{ margin: `${Grid._12} auto` }}>
              <SectionTitle
                title={data.catalog.buttonsSection.title}
                subtitle={data.catalog.buttonsSection.description}
              />
              <div
                data-qa-hook="trainingButtonContainer"
                className="grid-container grid-col-1 grid-sm-col-2 grid-md-col-3 grid-gap-24"
              >
                {data.catalog.buttonsSection.buttons.map(button => {
                  const link = button.link;
                  const buttonLinkType = getButtonLinkType(link);
                  const redirectUrl = () =>
                    buttonLinkType === 'training' ? redirectToTrainingLinkUrl(link) : redirectToSearchLinkUrl(link);
                  return (
                    <ActionRowButton data-qa-hook="trainingButtonItem" key={button.id} onClick={redirectUrl}>
                      {button.text} <i className="fas fa-chevron-right" />
                    </ActionRowButton>
                  );
                })}
              </div>
            </Container>
          )}
          {data.catalog?.viewAllSection && (
            <ViewAllTraining description={data.catalog.viewAllSection.description || ''} />
          )}
          <NewPurchasesOverlay domain="training" items={getRecentlyPaired()} />
        </>
      )}
    </>
  );
}
